/*
 * MANAGE NOTIFICATIONS (docs/design/stage3-alerts.md §4.2 and §5, decision 395).
 *
 * ITS OWN PAGE, not a sixth inline Settings section, on the Manage fencers
 * pattern: a Settings row navigates to `/settings/notifications`. Decision 395
 * ruled that explicitly, and the reason is size — the matrix is five groups by
 * as many channels as exist, with per-kind detail behind a disclosure, which is
 * a page rather than a panel.
 *
 * PER LIST (decision 394), because a user will want different alerts for
 * different lists — "me" against "people I know" is the case Kevin named. The
 * rows stay per list; what changed with multi-list item 4 is that this page
 * reads and writes across a SCOPE of them rather than one at a time.
 *
 * THE SCOPE IS THE SHARED CONTROL, not this page's own `<select>` (decisions
 * 512, 516). The select answered "which list am I editing" — one list, one
 * answer — and this page's question is the same one Active, Upcoming and Past
 * ask: which lists am I looking at. So `ListScope` renders here too, on the same
 * two conditions (510) written out below, and the same stored per-view mode
 * (502) resolved server-side.
 *
 * THE MERGE IS THE SERVER'S (516). `GET /api/me/alert-preferences` answers one
 * tree merged across the lists in scope, and a cell reads `mixed` where they
 * disagree — the same word decision 486 already uses for kinds within one list
 * that disagree, because a reader's next move is the same either way: open the
 * level and look. `PUT` applies a flip to every list in scope in ONE
 * transaction, so `saveFailed` below means "nothing was written" and needs no
 * partial-failure sentence (517).
 *
 * AN ACCOUNT WITH ONE LIST SEES WHAT IT ALWAYS SAW: no control, the same tree,
 * the same values. `multi_list` defaults off during the trial (decision 369) and
 * the picker needs a second list besides, so for every account in the trial this
 * page is simply "your notifications".
 *
 * WHAT IS A RULE AND WHAT IS A DEFAULT, and the page shows the difference by
 * showing nothing:
 *
 *   - Day-of × email is a RULE (decision 392) and is not offered. The server
 *     refuses it too, so this is not the only enforcement — but a toggle the
 *     product will never honour must not be rendered, because a control that
 *     does nothing is worse than an absent one.
 *   - Email is not an alert channel (decision 612). Email Summaries is the email
 *     surface — its own section in Settings, one message a day or a week — so
 *     this page has no email column. The server says which channels it
 *     delivers (`channels`), and this page renders one column per entry.
 *
 * SAVED ON CHANGE, NOT ON A BUTTON. Contract §2 already holds preferences
 * server-side "so a toggle flipped on a laptop is already flipped on the phone at
 * the venue", and a Save button on a page of toggles invites leaving without
 * pressing it. Each flip is one PUT carrying one cell (decision 394's partial
 * shape) — one request whatever the scope holds — and the response is the
 * recomputed matrix, so the screen learns whether its value became an override
 * or fell back to the default without asking again.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { ListScope } from "../components/ListScope";
import { PreferenceTree, type SetPreference } from "../components/PreferenceTree";
import { api, type User, type ViewAlertPreferences } from "../lib/api";
import type { NotificationNameFormat } from "../lib/format";
import { useMultiList } from "../lib/useMultiList";
import { useViewScope } from "../lib/useViewScope";
import { usePushEnrolment } from "../lib/usePushEnrolment";
import styles from "./ManageNotifications.module.css";

export interface ManageNotificationsProps {
  /**
   * The signed-in account, for the Notification Options section (decision 535).
   *
   * The WHOLE user and not just the one field, because saving goes through the
   * whole-object preferences PUT: a partial write resets what it omits, so the
   * caller has to hold every field. Threaded in from the route the way
   * `features` already is; a second `/api/me` here would be a second copy of
   * state the shell holds.
   */
  user: User;

  /**
   * Reports the preferences PUT's answer back to session state, so the choice
   * survives a navigation without a reload — the same callback Settings takes.
   */
  onUserChanged: (user: User) => void;

  /**
   * The caller's feature grants, from `/api/me` (decision 510's first
   * condition). Threaded in the way the route already threads it to Manage
   * fencers; the page cannot read it for itself without a second `/api/me`.
   */
  features: string[];

  /**
   * The VAPID application server key from /api/me (stage 4a).
   *
   * Passed in rather than fetched, because `pushManager.subscribe` has to run
   * inside the user's GESTURE handler — a network round trip between the tap and
   * the permission prompt loses the gesture and Safari refuses the prompt.
   *
   * Empty or absent means this build cannot enrol a device, which the control
   * treats as the same state a browser with no push support gives it.
   */
  pushPublicKey?: string;
}

export function ManageNotifications({
  user,
  onUserChanged,
  features,
  pushPublicKey,
}: ManageNotificationsProps) {
  const { t } = useTranslation();

  const [prefs, setPrefs] = useState<ViewAlertPreferences | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped to re-read the ladder after a scope change, on the routes' pattern:
  // one counter rather than a second fetch path, so the cancelled-on-unmount
  // guard covers both.
  const [reloads, setReloads] = useState(0);

  /*
   * THE TWO CONDITIONS, WRITTEN OUT HERE (decision 510). The FLAG decides
   * whether any multi-list chrome exists; the COUNT decides whether a picker
   * does, and this control needs both. Written out at the call site rather than
   * hoisted into a helper for 510's own reason: a shared "should chrome render"
   * would let the next control needing one of them get the other for free.
   *
   * `useMultiList` reads the lists whether or not the flag is held, because the
   * count cannot be known without reading. `useViewScope` is gated on both, so
   * an account that cannot see the control issues no view-lists request at all.
   */
  const { granted, lists } = useMultiList(features);
  const multiList = granted && lists.length > 1;

  const scope = useViewScope(
    "notifications",
    multiList,
    // A SCOPE CHANGE RE-READS THE PREFERENCES (513). The merge is the server's,
    // so a new scope is a new tree: nothing here can be recomputed locally the
    // way a regroup can.
    useCallback(() => setReloads((n) => n + 1), []),
  );

  useEffect(() => {
    let cancelled = false;
    api
      .viewAlertPreferences()
      .then((payload) => {
        if (!cancelled) setPrefs(payload);
      })
      .catch(() => {
        if (!cancelled) setError(t("error.couldNotLoad"));
      });
    return () => {
      cancelled = true;
    };
  }, [reloads, t]);

  /**
   * ONE VERB FOR EVERY RUNG (decision 486). The page used to have two -- one
   * writing a kind and one writing every kind in a group -- and the ladder
   * replaces both: a level is a (scope, selector) pair and the server resolves
   * what it means.
   *
   * That is not only tidier. The old group verb wrote N kind rows, so a reader
   * who turned "During a tournament" off recorded nothing about the LEVEL and
   * received every kind added afterwards. One row at the level they touched is
   * what fixes it, and it is the server that knows to clear the stale rows
   * beneath.
   */
  /**
   * Saving the name format, through the WHOLE-OBJECT preferences PUT.
   *
   * Every field goes on the wire, because the endpoint is a PUT and a partial
   * body resets what it omits — the client's copy of the user is the source for
   * the fields this page has no control over. That is the same rule Settings'
   * `savePreferences` follows, written out here rather than shared as a thinner
   * helper that could only send some of them.
   *
   * NOT the alert-preference endpoints. Those store per LIST (decisions 394,
   * 415, 516) and this is one value for every list and every kind at once, so
   * it has no list to be stored against.
   */
  const setNameFormat = (format: NotificationNameFormat) => {
    setError(null);
    api
      .preferences({
        theme: user.theme,
        reduce_motion: user.reduce_motion,
        upcoming_group_by: user.upcoming_group_by,
        past_group_by: user.past_group_by,
        notification_name_format: format,
        ...(user.ui_locale ? { ui_locale: user.ui_locale } : {}),
        // Round-trips unchanged, narrowed the way Settings narrows it: the
        // value came from the server and this screen has no control that sets
        // it.
        ...(user.persona_hint
          ? { persona_hint: user.persona_hint as "fencer" | "parent" | "coach" }
          : {}),
      })
      .then((body) => onUserChanged(body.user as User))
      .catch(() => setError(t("notifications.manage.saveFailed")));
  };

  const set: SetPreference = (rung, selector, channel, enabled) => {
    setError(null);
    api
      .setViewAlertPreferences([{ scope: rung, selector, channel, enabled }])
      .then(setPrefs)
      .catch(() => setError(t("notifications.manage.saveFailed")));
  };

  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <h1 className={styles.h1}>{t("notifications.manage.heading")}</h1>
        {/*
          A real anchor back to Settings, for the reason Manage fencers gives:
          this screen is reachable by URL, so a bookmark needs somewhere sensible
          to go and history.back() would have nowhere.
        */}
        <Link className={styles.back} to="/settings">
          {t("notifications.manage.back")}
        </Link>
      </div>

      {/*
        PLURAL CHROME REACHES THE COPY TOO (build rule 3). The second sentence
        explains the control, so it renders on exactly the two conditions the
        control does — an account with one list was being told "these settings
        apply to the list you pick", which reads as a caveat about something they
        do not have. Its wording moved with the control: the answer is now a set
        of lists and a flip writes to all of them, which is the part a reader
        cannot see from the tree.
      */}
      <p className={styles.intro}>
        {t("notifications.manage.intro")}
        {multiList ? ` ${t("notifications.manage.introMultiList")}` : ""}
      </p>

      <DeviceControl publicKey={pushPublicKey ?? ""} />

      {/*
        NOTIFICATION OPTIONS (decision 535) — how a notification READS, above
        the ladder that decides which ones arrive.

        UNCONDITIONAL, and both halves of that are deliberate. Not gated on
        `multi_list`, because the setting is account-wide and has nothing to do
        with how many lists an account holds. Not gated on push enrolment
        either, because it governs the in-app feed just as much as a push, and
        a reader who has not enrolled a device still has a feed to read.
      */}
      <section className={styles.options}>
        <h2 className={styles.optionsHeading}>{t("notifications.manage.options")}</h2>

        <div className={styles.row}>
          <span className={styles.rowLabel} id="name-format-label">
            {t("notifications.manage.nameFormat")}
          </span>
          {/*
            A three-way segmented control on Settings' theme-row pattern: one
            choice, so a radiogroup rather than three buttons, and a screen
            reader hears it as one. The UNSELECTED segments carry the red,
            because pressing the one you are on does nothing.
          */}
          <div className={styles.segmented} role="radiogroup" aria-labelledby="name-format-label">
            {(["first", "last", "full"] as const).map((choice) => {
              const selected = user.notification_name_format === choice;
              return (
                <button
                  key={choice}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={selected ? styles.segSelected : styles.segUnselected}
                  onClick={() => setNameFormat(choice)}
                >
                  {t(`notifications.manage.nameFormatOption.${choice}`)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/*
        THE SHARED CONTROL, WHERE THE `<select>` WAS (decisions 512, 516). Both
        conditions AND an arrived scope: the label states the scope, so a control
        rendered before the read lands would have nothing true to say.

        It keeps the select's own container, which is what gives it the same
        place on the page and the same spacing beneath it.
      */}
      {multiList && scope.scope !== null ? (
        <div className={styles.picker}>
          <ListScope
            lists={lists}
            scope={scope.scope}
            listIDs={scope.listIDs}
            saving={scope.saving}
            failed={scope.failed}
            onChange={scope.setScope}
          />
        </div>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {prefs === null ? null : <PreferenceTree prefs={prefs} onSet={set} />}
    </div>
  );
}

/**
 * THE DEVICE ENROLMENT CONTROL (stage 4a).
 *
 * FOUR STATES AND EACH IS A DIFFERENT SENTENCE, because they need different
 * things from the reader:
 *
 *   - UNSUPPORTED — one line, `push.unavailable`, and no control. This is the
 *     iOS-in-a-browser-tab case above all, and there is deliberately NO INSTALL
 *     TUTORIAL (Kevin's ruling): iOS delivers Web Push only to an installed PWA,
 *     the manifest is what makes installing possible, and explaining how to do
 *     it is not this screen's job. One device-conditional line and nothing else.
 *   - BLOCKED — the permission was refused permanently, and the browser will not
 *     ask again however many times this control is pressed. So the control is
 *     GONE rather than disabled: a button that cannot work is worse than none
 *     (contract §0), and the sentence says where the setting lives instead —
 *     one sentence for a browser tab and another for an installed app.
 *   - ENROLLED — a statement of fact plus the way out.
 *   - OTHERWISE — the offer, and a sentence saying what will actually arrive.
 *     A prompt closed without an answer lands here too, with one more line:
 *     the browser will ask again, so it is an offer still open, not a block.
 *
 * THE OFFER STATES WHAT IT WILL SEND, which is the part that is a promise rather
 * than chrome: `push.enableDetail` names the day-of kinds that default on
 * (decision 485's nine).
 *
 * IT NO LONGER CLOSES THE LIST (519, Kevin's copy, amending 498). The sentence
 * used to end "Nothing else, and never by email", which put decision 392's rule
 * in front of somebody deciding whether to trust this with their lock screen.
 * The rule is unchanged and enforced in three places that are not copy — the
 * channel matrix, the preference endpoint's refusal and the mail job's filter —
 * and stating it here was answering a question the reader had not asked, on the
 * one screen where every channel it concerns is visible anyway. What stays is
 * the list of what arrives, in this page's own voice rather than onboarding's
 * first-run one (498's split, which holds).
 *
 * NEVER ON LOAD. Nothing in this component asks for permission; the only call
 * path to `Notification.requestPermission` is inside the click handler, which is
 * also what the platforms require (see lib/push.ts).
 *
 * THE MECHANISM IS `usePushEnrolment` AND IS SHARED WITH ONBOARDING'S OFFER
 * (decision 498), which is the FencerSearch precedent applied to this control:
 * the support test, the VAPID-key test, the worker registration ahead of the tap
 * and the shape of the `enablePush` call are RULES, and two copies of them would
 * drift. What stayed here is the part that is not a rule — these four sentences.
 * The onboarding step shares none of them, because there every one of the states
 * above is a skipped step rather than an explanation.
 *
 * `sync: true` IS THIS PAGE'S AND NOT THE HOOK'S DEFAULT: re-asserting a
 * subscription the browser already holds is what makes the two sides
 * self-healing without a read endpoint, and it belongs to the page that manages
 * the enrolment rather than to a first-run offer somebody is walking past.
 */
function DeviceControl({ publicKey }: { publicKey: string }) {
  const { t } = useTranslation();

  const { supported, blocked, enrolled, busy, result, installed, enable, disable } =
    usePushEnrolment(publicKey, { sync: true });

  if (!supported) {
    return (
      <div className={styles.device}>
        <p className={styles.deviceState}>{t("push.unavailable")}</p>
      </div>
    );
  }

  // Checked after the support test and after enrolment is known, because a
  // device that IS enrolled cannot be blocked and the order matters on a browser
  // that reports `denied` for a permission it has already granted and revoked.
  //
  // WHERE THE BLOCK IS UNDONE DEPENDS ON HOW THE APP IS RUNNING, not on which
  // browser it is: a tab's is in the browser's settings for the site, an
  // installed app's is in the device's notification settings. The sentence
  // names the place and never a menu path, because directions written for one
  // browser misdirect every other.
  if (!enrolled && (blocked || result === "denied")) {
    return (
      <div className={styles.device}>
        <p className={styles.deviceState}>
          {installed ? t("push.blockedInstalled") : t("push.blocked")}
        </p>
      </div>
    );
  }

  if (enrolled) {
    return (
      <div className={styles.device}>
        <p className={styles.deviceState}>{t("push.enabled")}</p>
        <p className={styles.deviceDetail}>
          <button
            className={styles.deviceButton}
            type="button"
            onClick={disable}
            disabled={busy}
          >
            {t("push.disable")}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className={styles.device}>
      <button
        className={styles.deviceButton}
        type="button"
        onClick={() => void enable()}
        disabled={busy}
      >
        {t("push.enable")}
      </button>
      <p className={styles.deviceDetail}>{t("push.enableDetail")}</p>
      {/*
        DISMISSED IS NOT BLOCKED: the prompt was closed without an answer and
        the browser will ask again, so the button stays and the line says so.
      */}
      {result === "dismissed" ? (
        <p className={styles.deviceDetail}>{t("push.dismissed")}</p>
      ) : null}
      {result === "failed" ? (
        <p className={styles.deviceDetail} role="alert">
          {t("notifications.manage.saveFailed")}
        </p>
      ) : null}
    </div>
  );
}
